import { GARMENT_GROUPS, type GarmentGroup, type MeasurementPoint } from '@rosita/shared';
import { GARMENT_GROUP_LABELS } from '../product-settings/shared';

/** pointId → size → raw input string ("" = empty cell). */
export type MeasurementCells = Record<string, Record<string, string>>;

interface Props {
  points: MeasurementPoint[];
  /** Distinct size labels from the product's variants, already ordered. */
  sizes: string[];
  selectedPointIds: string[];
  onSelectedChange: (next: string[]) => void;
  cells: MeasurementCells;
  onCellsChange: (next: MeasurementCells) => void;
  note: string;
  onNoteChange: (next: string) => void;
}

const cellInputClass =
  'block w-full min-w-[4.5rem] rounded-md border border-admin-border bg-white px-2 py-1 text-right text-sm focus:border-admin-primary focus:outline-none';
const presetBtn =
  'rounded-full border border-admin-border bg-white px-3 py-1 text-xs text-admin-text-soft hover:border-admin-primary hover:text-admin-primary';
const noteClass =
  'mt-1 block w-full rounded-md border border-admin-border bg-white px-3 py-2 text-sm focus:border-admin-primary focus:outline-none';

/** Accepts digits with an optional single decimal (comma or dot). */
function sanitizeCm(raw: string): string {
  const normalized = raw.replace(',', '.').replace(/[^\d.]/g, '');
  const [intPart = '', ...rest] = normalized.split('.');
  const decimals = rest.join('').slice(0, 1);
  const trimmedInt = intPart.slice(0, 3);
  if (rest.length === 0) return trimmedInt;
  return `${trimmedInt}.${decimals}`;
}

export default function ProductMeasurementsEditor({
  points,
  sizes,
  selectedPointIds,
  onSelectedChange,
  cells,
  onCellsChange,
  note,
  onNoteChange,
}: Props): JSX.Element {
  const selected = new Set(selectedPointIds);
  const visiblePoints = points.filter((p) => p.isActive || selected.has(p.id));
  // Rows follow taxonomy order, not click order, so the grid matches the store.
  const rows = points.filter((p) => selected.has(p.id));

  const togglePoint = (id: string): void => {
    if (selected.has(id)) {
      onSelectedChange(selectedPointIds.filter((x) => x !== id));
    } else {
      onSelectedChange([...selectedPointIds, id]);
    }
  };

  const applyPreset = (group: GarmentGroup): void => {
    const ids = points
      .filter((p) => p.isActive && p.garmentGroups.includes(group))
      .map((p) => p.id);
    const merged = new Set([...selectedPointIds, ...ids]);
    onSelectedChange(Array.from(merged));
  };

  const setCell = (pointId: string, size: string, raw: string): void => {
    onCellsChange({
      ...cells,
      [pointId]: { ...(cells[pointId] ?? {}), [size]: sanitizeCm(raw) },
    });
  };

  const presetsAvailable = GARMENT_GROUPS.filter((g) =>
    points.some((p) => p.isActive && p.garmentGroups.includes(g))
  );

  if (points.length === 0) {
    return (
      <p className="text-sm text-admin-muted">
        Sem pontos de medida. Cria-os em Produtos &rsaquo; Configurações &rsaquo; Medidas.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <p className="-mt-2 text-sm text-admin-muted">
        Medidas da peça em centímetros, por tamanho. Escolhe os pontos que fazem sentido para
        esta peça e preenche a grelha. Podes deixar células em branco.
      </p>

      {presetsAvailable.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-admin-muted">Aplicar conjunto:</span>
          {presetsAvailable.map((g) => (
            <button key={g} type="button" onClick={() => applyPreset(g)} className={presetBtn}>
              {GARMENT_GROUP_LABELS[g]}
            </button>
          ))}
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2">
        {visiblePoints.map((p) => {
          const isSelected = selected.has(p.id);
          return (
            <label
              key={p.id}
              aria-pressed={isSelected}
              className={`inline-flex cursor-pointer select-none items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition-colors ${
                isSelected
                  ? 'border-admin-primary bg-admin-primary text-white shadow-sm'
                  : 'border-dashed border-admin-border bg-white text-admin-text-soft hover:border-admin-primary hover:text-admin-primary'
              }`}
            >
              <input
                type="checkbox"
                className="sr-only"
                checked={isSelected}
                onChange={() => togglePoint(p.id)}
              />
              <span aria-hidden="true" className="text-xs leading-none">
                {isSelected ? '✓' : '+'}
              </span>
              {p.name}
              {!p.isActive ? ' (inativa)' : ''}
            </label>
          );
        })}
      </div>

      {rows.length === 0 ? null : sizes.length === 0 ? (
        <p className="rounded-md bg-admin-surface-alt p-3 text-sm text-admin-muted">
          Adiciona variantes com tamanho para preencheres a grelha de medidas.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-admin-border bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-admin-surface-alt text-left text-xs uppercase tracking-wide text-admin-muted">
                <th scope="col" className="px-3 py-2 font-medium">
                  Medida (cm)
                </th>
                {sizes.map((s) => (
                  <th key={s} scope="col" className="px-3 py-2 text-right font-medium">
                    {s}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} className="border-t border-admin-border">
                  <th scope="row" className="whitespace-nowrap px-3 py-2 text-left font-normal">
                    {p.name}
                  </th>
                  {sizes.map((s) => (
                    <td key={s} className="px-2 py-1">
                      <input
                        type="text"
                        inputMode="decimal"
                        placeholder="—"
                        value={cells[p.id]?.[s] ?? ''}
                        onChange={(e) => setCell(p.id, s, e.target.value)}
                        aria-label={`${p.name}, tamanho ${s}, em centímetros`}
                        className={cellInputClass}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <label className="block text-sm font-medium text-admin-text-soft">
        Nota das medidas (opcional)
        <input
          type="text"
          maxLength={255}
          value={note}
          onChange={(e) => onNoteChange(e.target.value)}
          placeholder="Ex.: Medidas tiradas com a peça em plano."
          className={noteClass}
        />
      </label>
    </div>
  );
}
